// This is a rewrite of react-truncate library making it compatible with React 18.
// https://github.com/pablosichert/react-truncate

import React, { useEffect, useRef, useState } from "react";

const Truncate = ({
  children = "",
  ellipsis = "…",
  lines = 1,
  trimWhitespace = false,
  width = 0,
  onTruncate,
  ...spanProps
}) => {
  const [targetWidth, setTargetWidth] = useState(0);
  const elements = useRef({});
  const canvasContext = useRef(null);
  const replacedLinks = useRef([]);

  useEffect(() => {
    const canvas = document.createElement("canvas");
    canvasContext.current = canvas.getContext("2d");

    const handleResize = () => calcTargetWidth();
    window.addEventListener("resize", handleResize);

    calcTargetWidth(() => {
      if (elements.current.text && elements.current.text.parentNode) {
        elements.current.text.parentNode.removeChild(elements.current.text);
      }
    });

    return () => {
      if (elements.current.ellipsis?.parentNode) {
        elements.current.ellipsis.parentNode?.removeChild(
          elements.current.ellipsis,
        );
      }
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    calcTargetWidth();
  }, [children, width]);

  const extractReplaceLinksKeys = (content) => {
    let i = 0;
    replacedLinks.current = [];

    content.replace(/(<a[\s]+([^>]+)>((?:.(?!\<\/a\>))*.)<\/a>)/g, function () {
      const item = Array.prototype.slice.call(arguments, 1, 4);
      item.key = "[" + "@".repeat(item[2].length - 1) + "=" + i++ + "]";
      replacedLinks.current.push(item);

      content = content.replace(item[0], item.key);
    });

    return content;
  };

  const restoreReplacedLinks = (content) => {
    replacedLinks.current.forEach((item) => {
      content = content.replace(item.key, item[0]);
    });

    return createMarkup(content);
  };

  const innerText = (node) => {
    const div = document.createElement("div");
    const contentKey =
      "innerText" in window.HTMLElement.prototype ? "innerText" : "textContent";

    const content = node.innerHTML.replace(/\r\n|\r|\n/g, " ");
    div.innerHTML = extractReplaceLinksKeys(content);

    let text = div[contentKey];

    const test = document.createElement("div");
    test.innerHTML = "foo<br/>bar";

    if (test[contentKey].replace(/\r\n|\r/g, "\n") !== "foo\nbar") {
      div.innerHTML = div.innerHTML.replace(/<br.*?[\/]?>/gi, "\n");
      text = div[contentKey];
    }

    return text;
  };

  const calcTargetWidth = (callback) => {
    if (!elements.current.target) {
      return;
    }

    const targetWidth =
      width ||
      Math.floor(
        elements.current.target.parentNode.getBoundingClientRect().width,
      );

    if (!targetWidth) {
      return window.requestAnimationFrame(() => calcTargetWidth(callback));
    }

    const style = window.getComputedStyle(elements.current.target);
    const font = [
      style["font-weight"],
      style["font-style"],
      style["font-size"],
      style["font-family"],
    ].join(" ");
    canvasContext.current.font = font;

    setTargetWidth(targetWidth);
    callback?.();
  };

  const measureWidth = (text) => {
    return canvasContext.current.measureText(text).width;
  };

  const ellipsisWidth = (node) => {
    return node.offsetWidth;
  };

  const trimRight = (text) => {
    return text.replace(/\s+$/, "");
  };

  const createMarkup = (str) => {
    return <span dangerouslySetInnerHTML={{ __html: str }} />;
  };

  const getLines = () => {
    const text = innerText(elements.current.text);
    const textLines = text.split("\n").map((line) => line.split(" "));
    let didTruncate = true;
    const ellipsisWidthValue = ellipsisWidth(elements.current.ellipsis);

    const linesArray = [];
    for (let line = 1; line <= lines; line++) {
      const textWords = textLines[0];

      if (textWords.length === 0) {
        linesArray.push();
        textLines.shift();
        line--;
        continue;
      }

      let resultLine = textWords.join(" ");

      if (measureWidth(resultLine) <= targetWidth) {
        if (textLines.length === 1) {
          didTruncate = false;
          resultLine = restoreReplacedLinks(resultLine);
          linesArray.push(resultLine);
          break;
        }
      }

      if (line === lines) {
        const textRest = textWords.join(" ");
        let lower = 0;
        let upper = textRest.length - 1;

        while (lower <= upper) {
          const middle = Math.floor((lower + upper) / 2);
          const testLine = textRest.slice(0, middle + 1);

          if (measureWidth(testLine) + ellipsisWidthValue <= targetWidth) {
            lower = middle + 1;
          } else {
            upper = middle - 1;
          }
        }

        let lastLineText = textRest.slice(0, lower);

        if (trimWhitespace) {
          lastLineText = trimRight(lastLineText);

          while (!lastLineText.length && linesArray.length) {
            const prevLine = linesArray.pop();
            lastLineText = trimRight(prevLine);
          }
        }

        if (lastLineText.substr(lastLineText.length - 2) === "][") {
          lastLineText = lastLineText.substring(0, lastLineText.length - 1);
        }

        lastLineText = lastLineText.replace(/\[@+$/, "");
        lastLineText = restoreReplacedLinks(lastLineText);

        resultLine = (
          <span>
            {lastLineText}
            {ellipsis}
          </span>
        );
      } else {
        let lower = 0;
        let upper = textWords.length - 1;

        while (lower <= upper) {
          const middle = Math.floor((lower + upper) / 2);
          const testLine = textWords.slice(0, middle + 1).join(" ");

          if (measureWidth(testLine) <= targetWidth) {
            lower = middle + 1;
          } else {
            upper = middle - 1;
          }
        }

        if (lower === 0) {
          line = lines - 1;
          continue;
        }

        resultLine = textWords.slice(0, lower).join(" ");
        resultLine = restoreReplacedLinks(resultLine);
        textLines[0].splice(0, lower);
      }

      linesArray.push(resultLine);
    }

    if (typeof onTruncate === "function") {
      window.requestAnimationFrame(() => onTruncate(didTruncate));
    }

    return linesArray.map(renderLine);
  };

  const renderLine = (line, i, arr) => {
    if (i === arr.length - 1) {
      return <span key={i}>{line}</span>;
    } else {
      const br = <br key={i + "br"} />;

      if (line) {
        return [<span key={i}>{line}</span>, br];
      } else {
        return br;
      }
    }
  };

  let text;
  const mounted = !!(elements.current.target && targetWidth);

  if (typeof window !== "undefined" && mounted) {
    if (lines > 0) {
      text = getLines();
    } else {
      text = children;
      if (typeof onTruncate === "function") {
        onTruncate(false);
      }
    }
  }

  return (
    <span
      {...spanProps}
      ref={(targetEl) => {
        elements.current.target = targetEl;
      }}
    >
      <span
        style={{
          display: "block",
          maxWidth: spanProps.width > 0 ? `${spanProps.width}px` : "unset",
        }}
      >
        {text}
      </span>
      <span
        id="nysterious span"
        ref={(textEl) => {
          elements.current.text = textEl;
        }}
      >
        {children}
      </span>
      <span
        ref={(ellipsisEl) => {
          elements.current.ellipsis = ellipsisEl;
        }}
        style={{
          position: "fixed",
          visibility: "hidden",
          top: 0,
          left: 0,
        }}
      >
        {ellipsis}
      </span>
    </span>
  );
};

export default Truncate;
